/**
 * Project Detail Page
 *
 * Displays comprehensive analytics and links for a specific project.
 * Features:
 * - Aggregated statistics from all links in the project
 * - Per-link color differentiation in stacked charts
 * - Tabbed navigation for different analytics views
 * - Share button for project sharing
 *
 * @module pages/project-detail
 */

import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Link2,
  MousePointerClick,
  Trophy,
  ExternalLink,
  Copy,
  QrCode,
  BarChart3,
  Loader2,
  Layers,
  Activity,
  UserPlus,
  Users,
  Tag,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/client/lib/api';
import {
  formatNumber,
  formatRelativeTime,
  copyToClipboard,
  cn,
  truncateUrl,
} from '@/client/lib/utils';
import { useUIStore } from '@/client/stores/ui-store';
import { Badge } from '@/client/components/ui/badge';
import { getLinkColor } from '@/client/lib/color-utils';
import { Button } from '@/client/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/client/components/ui/card';

// =============================================================================
// Type Definitions
// =============================================================================

interface ProjectLink {
  id: string;
  shortCode: string;
  shortUrl: string;
  destinationUrl: string;
  title: string | null;
  tags?: string[];
  totalClicks: number;
  isActive: boolean;
  createdAt: string;
}

interface ProjectData {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  createdAt: string;
  links: ProjectLink[];
}

interface AnalyticsLink {
  id: string;
  shortCode: string;
  title: string | null;
  totalClicks: number;
  colorIndex: number;
}

interface DailyStat {
  date: string;
  clicks: number;
  uniqueVisitors: number;
  qrScans: number;
  directClicks: number;
  newVisitors: number;
  returningVisitors: number;
  byLink: Record<string, { clicks: number; uniqueVisitors: number }>;
}

interface ProjectAnalytics {
  project: {
    id: string;
    name: string;
    color: string;
    isDefault: boolean;
    createdAt: string;
    updatedAt: string;
  };
  links: AnalyticsLink[];
  totals: {
    clicks: number;
    uniqueVisitors: number;
    qrScans: number;
    directClicks: number;
    newVisitors: number;
    returningVisitors: number;
  };
  dailyStats: DailyStat[];
  aggregated: {
    countries: Record<string, number>;
    cities: Record<string, number>;
    devices: Record<string, number>;
    browsers: Record<string, number>;
    osTypes: Record<string, number>;
    referrers: Record<string, number>;
    referrerCategories: Record<string, number>;
    referrerPlatforms: Record<string, number>;
    utmSources: Record<string, number>;
    hours: Record<string, number>;
  };
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// =============================================================================
// Main Component
// =============================================================================

export function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { openQrModal, openShareModal } = useUIStore();

  // Fetch project basic info (for header and links table)
  const {
    data: project,
    isLoading: projectLoading,
    error: projectError,
  } = useQuery<ProjectData>({
    queryKey: ['project', id],
    queryFn: async () => {
      const response = await apiFetch(`/api/projects/${id}`);
      if (!response.ok) {
        if (response.status === 404) throw new Error('Project not found');
        throw new Error('Failed to fetch project');
      }
      return response.json();
    },
    enabled: !!id,
  });

  // Fetch project analytics summary (using default 30d range for stats cards)
  const { data: analytics } = useQuery<ProjectAnalytics>({
    queryKey: ['project-analytics', id],
    queryFn: async () => {
      const response = await apiFetch(`/api/projects/${id}/analytics`);
      if (!response.ok) throw new Error('Failed to fetch analytics');
      return response.json();
    },
    enabled: !!id,
  });

  // Calculate stats from project
  const stats = project
    ? {
        totalLinks: project.links.length,
        totalClicks:
          analytics?.totals.clicks ??
          project.links.reduce((sum, link) => sum + link.totalClicks, 0),
        activeLinks: project.links.filter((l) => l.isActive).length,
        uniqueVisitors: analytics?.totals.uniqueVisitors ?? 0,
      }
    : { totalLinks: 0, totalClicks: 0, activeLinks: 0, uniqueVisitors: 0 };

  // Find top performer
  const topPerformer = project?.links.reduce(
    (best, link) => (link.totalClicks > (best?.totalClicks ?? 0) ? link : best),
    null as ProjectLink | null
  );

  const handleCopyLink = async (shortUrl: string) => {
    await copyToClipboard(shortUrl);
    toast.success('Link copied to clipboard!');
  };

  const handleShareProject = () => {
    if (project) {
      openShareModal({
        id: project.id,
        shortCode: project.name,
        title: project.name,
        contextType: 'project',
      });
    }
  };

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="text-center py-12">
        <Layers className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
        <h2 className="text-xl font-semibold mb-2">Project not found</h2>
        <p className="text-muted-foreground mb-4">
          This project doesn&apos;t exist or you don&apos;t have access to it.
        </p>
        <button
          onClick={() => navigate('/projects')}
          className="inline-flex items-center gap-2 text-primary hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to projects
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/projects')}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3">
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${project.color}20` }}
            >
              <Layers className="h-5 w-5" style={{ color: project.color }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold">{project.name}</h1>
              <p className="text-sm text-muted-foreground">
                {stats.totalLinks} {stats.totalLinks === 1 ? 'link' : 'links'}
                {project.isDefault && (
                  <span className="ml-2 px-1.5 py-0.5 rounded text-xs bg-amber-500/20 text-amber-600">
                    Default
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Share Button */}
        <button
          onClick={handleShareProject}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border hover:bg-muted transition-colors"
        >
          <UserPlus className="h-4 w-4" />
          Share Project
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-4">
        {/* Total Links */}
        <div className="group rounded-2xl border bg-card p-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative flex items-center gap-3">
            <div
              className="h-12 w-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${project.color}15` }}
            >
              <Link2 className="h-6 w-6" style={{ color: project.color }} />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Links</p>
              <p className="text-2xl font-bold tabular-nums">{formatNumber(stats.totalLinks)}</p>
            </div>
          </div>
        </div>

        {/* Total Clicks */}
        <div className="group rounded-2xl border bg-card p-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(172,66%,50%)]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[hsl(172,66%,50%)]/15 flex items-center justify-center">
              <MousePointerClick className="h-6 w-6 text-[hsl(172,66%,50%)]" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Clicks</p>
              <p className="text-2xl font-bold tabular-nums">{formatNumber(stats.totalClicks)}</p>
            </div>
          </div>
        </div>

        {/* Unique Visitors */}
        <div className="group rounded-2xl border bg-card p-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(45,93%,58%)]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[hsl(45,93%,58%)]/15 flex items-center justify-center">
              <Users className="h-6 w-6 text-[hsl(45,93%,58%)]" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Unique Visitors</p>
              <p className="text-2xl font-bold tabular-nums">
                {formatNumber(stats.uniqueVisitors)}
              </p>
            </div>
          </div>
        </div>

        {/* Top Performer */}
        <div className="group rounded-2xl border bg-card p-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-amber-500/15 flex items-center justify-center">
              <Trophy className="h-6 w-6 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-muted-foreground">Top Performer</p>
              {topPerformer ? (
                <Link
                  to={`/links/${topPerformer.shortCode}`}
                  className="text-base font-bold hover:text-primary truncate block"
                >
                  {topPerformer.title || topPerformer.shortCode}
                </Link>
              ) : (
                <p className="text-base text-muted-foreground">No links yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Analytics CTA */}
      <Card className="rounded-2xl border-dashed bg-muted/30 py-0">
        <CardContent className="p-8 text-center">
          <div className="size-16 rounded-2xl bg-gradient-to-br from-blue-500/20 to-indigo-500/20 flex items-center justify-center mx-auto mb-4">
            <Activity className="size-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Detailed Project Analytics</h3>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            View comprehensive performance data for this project, including aggregated timeline and
            geographic distribution in the Analytics Hub.
          </p>
          <Button asChild className="rounded-xl" size="lg">
            <Link to={`/analytics?projectIds=${id}`}>
              View Project Analytics
              <ArrowRight className="size-4 ml-2" />
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Link Performance Table */}
      {project && project.links.length > 0 && (
        <Card className="rounded-2xl py-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Project Links</CardTitle>
            <CardDescription>Performance summary of links in this project</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b text-sm">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Link</th>
                    <th className="text-left px-4 py-3 font-medium">Destination</th>
                    <th className="text-left px-4 py-3 font-medium">Clicks</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                    <th className="text-left px-4 py-3 font-medium">Created</th>
                    <th className="text-right px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {project.links.map((link, idx) => (
                    <tr
                      key={link.id}
                      className={cn('hover:bg-muted/30', !link.isActive && 'opacity-60')}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: getLinkColor(idx) }}
                          />
                          <a
                            href={link.shortUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            {link.shortUrl.replace('https://', '')}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                        {link.title && (
                          <div className="text-xs text-muted-foreground mt-0.5 ml-4">
                            {link.title}
                          </div>
                        )}
                        {/* Tags */}
                        {link.tags && link.tags.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap mt-1 ml-4">
                            <Tag className="size-3 text-muted-foreground" />
                            {link.tags.slice(0, 2).map((tag) => (
                              <Badge
                                key={tag}
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0 font-normal"
                              >
                                {tag}
                              </Badge>
                            ))}
                            {link.tags.length > 2 && (
                              <span className="text-[10px] text-muted-foreground">
                                +{link.tags.length - 2} more
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <a
                          href={link.destinationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-muted-foreground hover:text-foreground"
                          title={link.destinationUrl}
                        >
                          {truncateUrl(link.destinationUrl)}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium">{formatNumber(link.totalClicks)}</span>
                      </td>
                      <td className="px-4 py-3">
                        {link.isActive ? (
                          <span className="flex items-center gap-1.5 text-green-600 text-sm">
                            <span className="h-2 w-2 rounded-full bg-green-500" />
                            Active
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-red-500 text-sm">
                            <span className="h-2 w-2 rounded-full bg-red-500" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {formatRelativeTime(link.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleCopyLink(link.shortUrl)}
                            className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title="Copy link"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openQrModal(link.shortCode, link.shortUrl)}
                            className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title="Generate QR code"
                          >
                            <QrCode className="h-4 w-4" />
                          </button>
                          <Link
                            to={`/links/${link.shortCode}`}
                            className="p-2 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
                            title="View analytics"
                          >
                            <BarChart3 className="h-4 w-4" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
